import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Paper,
  Backdrop,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Container
} from '@mui/material';
import { GridColDef, GridColumnVisibilityModel } from '@mui/x-data-grid';
import { jaJP } from '@mui/x-data-grid/locales';
import {
  DataGridPremium,
  GridColumnResizeParams,
  GridFilterItem,
  GridFilterModel,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
  useGridApiRef,
  GridDensity
} from '@mui/x-data-grid-premium';
import { useQueryClient } from '@tanstack/react-query';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { saveAs } from 'src/utils/fileSaver';
import { FileText } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { FC, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { CreateButton } from 'src/components/ui/actionGroup/CreateButton';
import { ExportButton } from 'src/components/ui/actionGroup/ExportButton';
import { FileImportButton } from 'src/components/ui/actionGroup/FileImportButton';
import { CustomLoadingOverlay } from 'src/components/utils/CustomLoadingOverlay';
import { appBarHeight } from 'src/constants/layout';
import { fiveZeroZeroErrorMessage } from 'src/constants/messages';
import LicenseContext from 'src/contexts/LicenseContext';
import { ContractEntity } from 'src/entities/contractEntity';
import { RansackPredicateEntity } from 'src/entities/RansackPredicate.entity';
import { queryKey, useContractsRequest as usePaginationRequest } from 'src/hooks/useContracts.request';
import { useMutationContract } from 'src/hooks/useQueryContracts';
import { dataGridUtil } from 'src/utils/dataGrid.util';
import operatorValueUtil from 'src/utils/operatorValue.util';
import stringUtil from 'src/utils/string.util';

import { SelectAllSnackbars } from '../common/SelectAllSnackbars';

import { Columns } from './Columns';
import DocumentPreviewDialog from './DocumentPreviewDialog';
import FormDialog from './FormDialog';
import PricingDetailsDialog from './PricingDetailsDialog';

const V2ContractsPresenter: FC = memo(() => {
  const localStorageItemPrefix = 'contract-columns';
  const { enqueueSnackbar } = useSnackbar();
  const licenseContext = useContext(LicenseContext);
  const queryClient = useQueryClient();

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(30);
  const [rowCount, setRowCount] = useState<number>(0);
  const [sortCondition, setSortCondition] = useState<string>('');
  const [orConditions, setOrConditions] = useState<RansackPredicateEntity[]>([]);
  const [andConditions, setAndConditions] = useState<RansackPredicateEntity[]>([]);
  const [searchCondition, setSearchCondition] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [density, setDensity] = useState<GridDensity>('standard');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [entity, setEntity] = useState<ContractEntity | undefined>(undefined);
  const [dialogIsOpen, setDialogIsOpen] = useState<boolean>(false);
  const [documentPreviewDialogIsOpen, setDocumentPreviewDialogIsOpen] = useState<boolean>(false);
  const [documentPreviewEntity, setDocumentPreviewEntity] = useState<ContractEntity | undefined>(undefined);
  const [pricingDetailsDialogIsOpen, setPricingDetailsDialogIsOpen] = useState<boolean>(false);
  const [pricingDetailsEntity, setPricingDetailsEntity] = useState<ContractEntity | undefined>(undefined);
  const [currentTab, setCurrentTab] = useState<number>(0); // 0: 請求契約, 1: 支払い契約
  const [contractTypeFilter, setContractTypeFilter] = useState<'billing' | 'payment' | 'all'>('billing');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [importFile, setImportFile] = useState<File | undefined>(undefined);

  const { data: requestData, isLoading: requestIsLoading } = usePaginationRequest(page, sortCondition, searchCondition);
  const apiRef = useGridApiRef();

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (sortModel.length === 0) {
      setSortCondition('');
      return;
    }

    setSortCondition(
      [
        stringUtil.toSnakeCase(sortModel[0].field),
        sortModel[0].sort
      ].join('+')
    );
  };

  const onFilterChange = ((filterModel: GridFilterModel) => {
    const items = filterModel?.items;

    if (!items || items.length === 0) {
      setOrConditions([]);
      setAndConditions([]);
      return;
    }

    const ransackPredicates: RansackPredicateEntity[] = items.flatMap((item: GridFilterItem) => operatorValueUtil.convertToRansackPredicate(
      stringUtil.toSnakeCase(item.field),
      item.operator,
      item.value as string | string[] | undefined,
      filterModel.logicOperator
    ));

    setOrConditions(ransackPredicates.filter((predicate) => predicate?.link === 'or'));
    setAndConditions(ransackPredicates.filter((predicate) => predicate?.link === 'and'));
  });

  const { deleteContracts } = useMutationContract();

  const toggleDialogIsOpen = () => {
    setEntity(null);
    setDialogIsOpen(!dialogIsOpen);
  };

  const openEditDialog = (contractEntity: ContractEntity) => {
    setEntity(contractEntity);
    setDialogIsOpen(true);
  };

  const handleDeleteSingleContract = useCallback((contractId: number, contractorName: string) => {
    const confirmMsg = `契約「${contractorName}」を削除します。よろしいですか?`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    setIsLoading(true);
    deleteContracts.mutateAsync([contractId])
      .then(() => {
        enqueueSnackbar('契約を削除しました');
      })
      .catch((e: AxiosError<{ message: string; }>) => {
        enqueueSnackbar(e.response?.data?.message || '削除に失敗しました', { variant: 'error' });
      })
      .finally(() => {
        void queryClient
          .invalidateQueries({ queryKey: [queryKey] })
          .finally(() => {
            setIsLoading(false);
          });
      });
  }, [deleteContracts, enqueueSnackbar, queryClient]);

  const handleCopyContract = useCallback((originalEntity: ContractEntity) => {
    const copiedEntity: ContractEntity = {
      ...originalEntity,
      id: undefined,
      pricing_settings: originalEntity.pricing_settings?.map((setting) => ({
        ...setting,
        area_settings: setting.area_settings?.map((area) => ({ ...area })) ?? [],
        temperature_types: [...(setting.temperature_types ?? [])],
        vehicle_types: setting.vehicle_types ? [...setting.vehicle_types] : [],
        distance_weight_settings: setting.distance_weight_settings?.map((dw) => ({ ...dw })) ?? [],
      })) ?? [],

      documents: [],
      document_signed_ids: [],
      has_document: false,

      created_at: undefined,
      updated_at: undefined,

      start_date: new Date().toISOString().split('T')[0],
      end_date: undefined,
    };

    openEditDialog(copiedEntity);
  }, []);

  const openDocumentPreviewDialog = (contractEntity: ContractEntity) => {
    setDocumentPreviewEntity(contractEntity);
    setDocumentPreviewDialogIsOpen(true);
  };

  const closeDocumentPreviewDialog = () => {
    setDocumentPreviewDialogIsOpen(false);
    setDocumentPreviewEntity(undefined);
  };

  const openPricingDetailsDialog = (contractEntity: ContractEntity) => {
    setPricingDetailsEntity(contractEntity);
    setPricingDetailsDialogIsOpen(true);
  };

  const closePricingDetailsDialog = () => {
    setPricingDetailsDialogIsOpen(false);
    setPricingDetailsEntity(undefined);
  };

  const dataGridColumns: GridColDef[] = useMemo(() => Columns(openEditDialog, openDocumentPreviewDialog, handleDeleteSingleContract, handleCopyContract, openPricingDetailsDialog).filter((col) => {
    if (licenseContext?.config?.selectable_companies?.length > 1) {
      return true;
    }
    return col.field !== 'company_name';
  }), [licenseContext?.config, handleDeleteSingleContract, handleCopyContract]);

  const onSelectionModelChange = (rowSelectionModel: GridRowSelectionModel) => {
    const ids = Array.from(rowSelectionModel.ids);
    if (rowSelectionModel.type === 'include') {
      setSelectedIds(ids.map((it) => Number(it)));
    } else if (rowSelectionModel.type === 'exclude') {
      const allIds = requestData.data.map((it) => it.id);
      setSelectedIds(allIds.filter((id) => !ids.includes(id)));
    }
  };

  const onRowDoubleClick = (params: GridRowParams) => {
    setEntity(params.row as ContractEntity);
    setDialogIsOpen(true);
  };

  const columnVisibilityModel = () => {
    const savedColumnVisibilities = dataGridUtil.getSavedColumnVisibilities(localStorageItemPrefix);
    return savedColumnVisibilities;
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setContractTypeFilter(newValue === 0 ? 'billing' : 'payment');
    setPage(0); // タブ切り替え時はページをリセット
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setPage(0); // フィルター変更時はページをリセット
  };

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImportFile(file);
    }
  };

  const handleNewContract = () => {
    setEntity(null);
    setDialogIsOpen(true);
  };

  useEffect(() => {
    const typeCondition = contractTypeFilter !== 'all'
      ? `contract_type_eq=${contractTypeFilter}`
      : '';

    const statusCondition = statusFilter !== 'all'
      ? `status_eq=${statusFilter}`
      : '';

    const searchConditionParam = searchQuery
      ? `contractor_name_cont=${searchQuery}`
      : '';

    setSearchCondition(
      [
        operatorValueUtil.convertToQueryParams('or_conditions', orConditions),
        operatorValueUtil.convertToQueryParams('and_conditions', andConditions),
        typeCondition,
        statusCondition,
        searchConditionParam,
      ].filter((maybe) => maybe).join('&')
    );
  }, [andConditions, orConditions, contractTypeFilter, statusFilter, searchQuery]);

  useEffect(() => {
    if (!requestData) return;

    setRowCount(requestData?.totalCount || 0);
  }, [requestData]);

  useEffect(() => {
    if (!importFile) return;

    const requestPath = '/api/v5/master/contracts/import';

    const data = new FormData();
    data.append('file', importFile);

    setIsLoading(true);
    enqueueSnackbar('インポートを開始します');

    axios
      .post(requestPath, data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then(
        (response: AxiosResponse<{ severity: string; message: string }>) => {
          enqueueSnackbar(response.data.message);
        }
      )
      .catch((e) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (e.response.status === 400) {
          enqueueSnackbar(
            '取り込めない契約情報がありました、データをご確認ください。'
          );
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
          const blob = new Blob([e.response.data], {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
            type: e.response.data.type
          });
          saveAs(blob, 'errors.txt');
        } else {
          enqueueSnackbar(fiveZeroZeroErrorMessage);
        }
      })
      .finally(() => {
        setImportFile(undefined);
        setIsLoading(false);
        void queryClient.invalidateQueries({
          queryKey: [queryKey]
        });
      });
  }, [enqueueSnackbar, importFile, queryClient]);

  const handleExport = () => {
    const searchParams = new URLSearchParams();

    // フィルター・検索条件
    if (searchCondition) {
      const conditionParams = new URLSearchParams(searchCondition);
      conditionParams.forEach((value, key) => {
        searchParams.append(key, value);
      });
    }

    // ソート条件
    if (sortCondition) {
      searchParams.append('sort_condition', sortCondition);
    }

    // 選択されたIDがある場合（部分エクスポート）
    if (selectedIds.length > 0) {
      searchParams.set('ids', selectedIds.join(','));
    }

    const requestUrl = `/api/v5/master/contracts/export?${searchParams.toString()}`;
    window.open(requestUrl, '_blank', 'noopener');
  };

  const [disableImport, setDisableImport] = useState<boolean>(false);
  const [importTooltip, setImportTooltip] = useState<string>('');
  const importRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (licenseContext?.config?.selected_company_id === 0) {
      setDisableImport(true);
      setImportTooltip('インポート対象の事業所を選択してください');
    } else {
      setDisableImport(false);
      setImportTooltip('');
    }
  }, [licenseContext?.config]);

  // タブごとの件数を取得（モックデータから返される）
  const billingCount = requestData?.billingCount || 0;
  const paymentCount = requestData?.paymentCount || 0;

  return (
    <>
      <Helmet>
        <title>契約管理</title>
      </Helmet>
      <Box
        sx={{
          bgcolor: 'background.paper',
          height: `calc(100vh - ${appBarHeight}px)`,
          display: 'flex',
          flexDirection: 'column',
          p: 1,
          overflow: 'hidden',
        }}
      >
        <FormDialog
          dialogIsOpen={dialogIsOpen}
          toggleDialogIsOpen={toggleDialogIsOpen}
          setDialogIsOpen={setDialogIsOpen}
          entity={entity}
        />

        <DocumentPreviewDialog
          open={documentPreviewDialogIsOpen}
          onClose={closeDocumentPreviewDialog}
          documents={documentPreviewEntity?.documents || []}
          contractorName={documentPreviewEntity?.contractor_name}
        />

        <PricingDetailsDialog
          open={pricingDetailsDialogIsOpen}
          onClose={closePricingDetailsDialog}
          pricingSettings={pricingDetailsEntity?.pricing_settings || []}
          contractorName={pricingDetailsEntity?.contractor_name}
        />

        <SelectAllSnackbars
          totalPageCount={requestData?.totalPageCount || 1}
          totalCount={requestData?.totalCount || 0}
          perPage={pageSize}
          allIds={requestData?.allIds || []}
          selectedIds={selectedIds}
          apiRef={apiRef}
          setSelectedIds={setSelectedIds}
        />

        <Container
          maxWidth={false}
          disableGutters
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            flex: 1,
            mb: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {/* ヘッダー部分 */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5, fontSize: '14pt' }}>
              契約管理
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '8pt' }}>
              請求・支払いに関する契約条件を管理します
            </Typography>
          </Box>

          {/* アクションバー */}
          <Paper elevation={1} sx={{ p: 2, mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  size="small"
                  placeholder="契約先名で検索..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 300 }}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>ステータス</InputLabel>
                  <Select
                    value={statusFilter}
                    label="ステータス"
                    onChange={(e) => handleStatusFilterChange(e.target.value)}
                  >
                    <MenuItem value="all">すべて</MenuItem>
                    <MenuItem value="active">有効</MenuItem>
                    <MenuItem value="inactive">無効</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FileImportButton
                  disabled={disableImport}
                  tooltipTitle={importTooltip}
                  onChange={handleImport}
                  inputRef={importRef}
                  accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                />
                <ExportButton onClick={handleExport} label="エクスポート" />
                <CreateButton onClick={handleNewContract} label="新規登録" />
              </Box>
            </Box>
          </Paper>

          <Box sx={{width: '100%', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <Paper
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <Tabs
                value={currentTab}
                onChange={handleTabChange}
                sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
              >
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FileText size={18} />
                      請求契約
                      <Chip label={`${billingCount}件`} size="small" color={currentTab === 0 ? "primary" : undefined} />
                    </Box>
                  }
                />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FileText size={18} />
                      支払い契約
                      <Chip label={`${paymentCount}件`} size="small" color={currentTab === 1 ? "primary" : undefined} />
                    </Box>
                  }
                />
              </Tabs>
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <DataGridPremium
                  apiRef={apiRef}
                  rows={requestData?.data || []}
                  columns={dataGridColumns}
                  pagination
                  disableRowGrouping
                  density={density}
                  onDensityChange={setDensity}
                  initialState={{
                    columns: { columnVisibilityModel: columnVisibilityModel() }
                  }}
                  slots={{
                    loadingOverlay: CustomLoadingOverlay
                  }}
                  localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
                  loading={requestIsLoading || isLoading}
                  onRowDoubleClick={onRowDoubleClick}
                  sortingMode="server"
                  paginationMode="server"
                  filterMode="server"
                  paginationModel={{ page, pageSize }}
                  rowCount={rowCount}
                  onPaginationModelChange={(model) => {
                    setPage(model.page);
                    setPageSize(model.pageSize);
                  }}
                  onSortModelChange={handleSortModelChange}
                  onFilterModelChange={onFilterChange}
                  onColumnWidthChange={(params: GridColumnResizeParams) => {
                    dataGridUtil.setSavedColumnWidth(localStorageItemPrefix, params);
                  }}
                  onColumnVisibilityModelChange={(newModel: GridColumnVisibilityModel) => {
                    dataGridUtil.setSavedColumnVisibilities(localStorageItemPrefix, newModel);
                  }}
                  pageSizeOptions={[30]}
                  disableAggregation
                  disablePivoting
                  getRowHeight={() => 'auto'}
                  sx={{
                    height: '100%',
                    width: '100%',
                    '& .MuiDataGrid-virtualScroller': {
                      overflow: 'auto',
                    },
                    '& .MuiDataGrid-cell': {
                      display: 'flex',
                      alignItems: 'center',
                      py: 1,
                    },
                  }}
                />
              </Box>
            </Paper>
          </Box>
        </Container>

        <Backdrop
          sx={{ color: '#fff' }}
          open={isLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
    </>
  );
});

export default V2ContractsPresenter;
